/**
 * Single source for Legacy RTL volume / delta / trades strike rows:
 * bid & ask volumes, four flow buckets, and premium = ask×last×100 / bid×last×100 per row.
 * Net premium = (boughtCall − soldCall) + (soldPut − boughtPut) premium, not netVolume × single price.
 */
import { ScannerData } from "../types/ScannerData";
import { DataMode } from "../types/DataMode";
import type { OptionsScannerData } from "./optionsUtils";
import { getFieldValue } from "./dataModeUtils";
import { getUnderlyingSymbol, universalStrikeSort } from "./filterUtils";
import { getMappedSymbol } from "./optionsUtils";

export interface LegacyStrikeAggRow {
    strike: string;
    boughtCalls: number;
    soldCalls: number;
    boughtPuts: number;
    soldPuts: number;
    boughtCallPremium: number;
    soldCallPremium: number;
    boughtPutPremium: number;
    soldPutPremium: number;
    /** (boughtCallPrem − soldCallPrem) + (soldPutPrem − boughtPutPrem) */
    netPremium: number;
    /** Volume-based flow: (boughtCalls + soldPuts) − (soldCalls + boughtPuts) */
    netDirectionalVolume: number;
    underlyingPrice: number;
    underlyingSymbol: string;
}

export function normalizeStrikeKey(
    strike: string | number | undefined | null,
): string {
    if (strike === undefined || strike === null) return "";
    const n = Number(strike);
    if (!Number.isFinite(n)) return String(strike).trim();
    return String(n);
}

/**
 * Same bid/ask resolution as legacy volume charts when one side is missing: split total Volume
 * by option type (calls vs puts) so Volume, Delta, and Trades panes agree.
 */
export function computeEffectiveBidAskVolumes(
    item: ScannerData,
    selectedDataMode: DataMode,
): { bid: number; ask: number; mainVolume: number } {
    const bidVolume = parseFloat(
        String(getFieldValue(item, "Bid Volume", selectedDataMode) || "0"),
    );
    const askVolume = parseFloat(
        String(getFieldValue(item, "Ask Volume", selectedDataMode) || "0"),
    );
    const mainVolume = parseFloat(String(item["Volume"] || "0"));
    const raw = String(item["Side"] || item["Option Type"] || "").toUpperCase();
    const optionType =
        raw === "C" || raw === "CALL"
            ? "C"
            : raw === "P" || raw === "PUT"
              ? "P"
              : raw.startsWith("C")
                ? "C"
                : raw.startsWith("P")
                  ? "P"
                  : "";

    let effectiveBid = bidVolume;
    let effectiveAsk = askVolume;

    if ((bidVolume === 0 || askVolume === 0) && mainVolume > 0 && optionType) {
        if (optionType === "C") {
            if (bidVolume === 0) {
                effectiveBid = mainVolume * 0.4;
                effectiveAsk = askVolume > 0 ? askVolume : mainVolume * 0.6;
            } else if (askVolume === 0) {
                effectiveAsk = mainVolume * 0.6;
                effectiveBid = bidVolume > 0 ? bidVolume : mainVolume * 0.4;
            }
        } else if (optionType === "P") {
            if (bidVolume === 0) {
                effectiveBid = mainVolume * 0.6;
                effectiveAsk = askVolume > 0 ? askVolume : mainVolume * 0.4;
            } else if (askVolume === 0) {
                effectiveAsk = mainVolume * 0.4;
                effectiveBid = bidVolume > 0 ? bidVolume : mainVolume * 0.6;
            }
        }
    }
    if (bidVolume === 0 && askVolume === 0 && mainVolume > 0 && optionType) {
        if (optionType === "C") {
            effectiveAsk = mainVolume * 0.6;
            effectiveBid = mainVolume * 0.4;
        } else if (optionType === "P") {
            effectiveBid = mainVolume * 0.6;
            effectiveAsk = mainVolume * 0.4;
        }
    }
    return { bid: effectiveBid, ask: effectiveAsk, mainVolume };
}

/**
 * Premium dollars use: contracts × price × 100. Prefer Last; if missing/zero, use Mid or theoretical value from the same row (no synthetic prices).
 */
export function effectiveOptionPriceForPremium(item: ScannerData): number {
    const last = parseFloat(String(item["Last"] ?? ""));
    if (Number.isFinite(last) && last > 0) return last;
    const ext = item as unknown as Record<string, unknown>;
    const mid = parseFloat(String(ext["Mid"] ?? ext["mid"] ?? ""));
    if (Number.isFinite(mid) && mid > 0) return mid;
    const tv = parseFloat(String(item["Theoretical Value"] ?? ""));
    if (Number.isFinite(tv) && tv > 0) return tv;
    return 0;
}

/** Same option classification as legacy volume charts (Side / Option Type). */
export function parseOptionSide(item: ScannerData): "CALL" | "PUT" | null {
    const optionTypeStr = String(
        item["Side"] || item["Option Type"] || "",
    ).toUpperCase();
    if (!optionTypeStr) return null;
    if (optionTypeStr === "C" || optionTypeStr === "CALL") return "CALL";
    if (optionTypeStr === "P" || optionTypeStr === "PUT") return "PUT";
    if (optionTypeStr.startsWith("C")) return "CALL";
    if (optionTypeStr.startsWith("P")) return "PUT";
    return null;
}

/**
 * Aggregate scanner rows by strike: volumes and premiums using the same rules for all legacy views.
 *
 * Per contract row (same data mode for bid/ask volume):
 *   boughtPremium = askVol × price × 100
 *   soldPremium   = bidVol × price × 100
 * Per strike:
 *   netCallPremium = boughtCallPremium − soldCallPremium
 *   netPutPremium  = soldPutPremium − boughtPutPremium  (sell-put flow minus buy-put flow)
 *   netPremium     = netCallPremium + netPutPremium
 *
 * Accepts raw or `processOptionsData` rows (compatible shapes).
 */
export function aggregateLegacyVolumeByStrike(
    filteredData: (ScannerData | OptionsScannerData)[],
    selectedDataMode: DataMode,
    isSymbolMappingEnabled: boolean,
): LegacyStrikeAggRow[] {
    const strikeData: Record<
        string,
        {
            strike: string;
            boughtCalls: number;
            soldCalls: number;
            boughtPuts: number;
            soldPuts: number;
            boughtCallPremium: number;
            soldCallPremium: number;
            boughtPutPremium: number;
            soldPutPremium: number;
            underlyingPrice: number;
            underlyingSymbol: string;
        }
    > = {};

    for (const raw of filteredData) {
        const item = raw as ScannerData;
        const strikeKey = normalizeStrikeKey(
            item.Strike as string | number | undefined,
        );
        if (!strikeKey) continue;

        const optionType = parseOptionSide(item);
        if (!optionType) continue;

        const underlying = getMappedSymbol(
            getUnderlyingSymbol(item),
            isSymbolMappingEnabled,
        );

        // Group by (underlying, strike) — same grouping as Data Table filters
        const groupKey = `${underlying}::${strikeKey}`;

        if (!strikeData[groupKey]) {
            strikeData[groupKey] = {
                strike: strikeKey,
                boughtCalls: 0,
                soldCalls: 0,
                boughtPuts: 0,
                soldPuts: 0,
                boughtCallPremium: 0,
                soldCallPremium: 0,
                boughtPutPremium: 0,
                soldPutPremium: 0,
                underlyingPrice: 0,
                underlyingSymbol: underlying,
            };
        }

        // Use backend's pre-computed delta fields — same source as Data Table
        const deltaAcc = parseFloat(
            String(
                getFieldValue(item, "Delta accumelated", selectedDataMode) ||
                    "0",
            ),
        );
        const deltaAccPremium = parseFloat(
            String(
                getFieldValue(
                    item,
                    "Delta Accumulated Premium",
                    selectedDataMode,
                ) || "0",
            ),
        );

        // Bid/Ask volumes for bar display only
        const { bid: effectiveBid, ask: effectiveAsk } =
            computeEffectiveBidAskVolumes(item, selectedDataMode);

        if (optionType === "CALL") {
            strikeData[groupKey].boughtCalls += effectiveAsk;
            strikeData[groupKey].soldCalls += effectiveBid;
            // Net delta: positive deltaAcc on calls = bullish (bought calls); negative = bearish (sold calls)
            strikeData[groupKey].boughtCallPremium += Math.max(
                0,
                deltaAccPremium,
            );
            strikeData[groupKey].soldCallPremium += Math.max(
                0,
                -deltaAccPremium,
            );
        } else {
            strikeData[groupKey].boughtPuts += effectiveAsk;
            strikeData[groupKey].soldPuts += effectiveBid;
            // Net delta: negative deltaAcc on puts = bullish (sold puts); positive = bearish (bought puts)
            strikeData[groupKey].soldPutPremium += Math.max(0, deltaAccPremium);
            strikeData[groupKey].boughtPutPremium += Math.max(
                0,
                -deltaAccPremium,
            );
        }

        if (item["Underlying Price"]) {
            const up = parseFloat(String(item["Underlying Price"]));
            if (!Number.isNaN(up) && up > 0) {
                strikeData[groupKey].underlyingPrice = up;
            }
        }
    }

    const rows: LegacyStrikeAggRow[] = Object.values(strikeData).map((s) => {
        const netCallPremium = s.boughtCallPremium - s.soldCallPremium;
        const netPutPremium = s.soldPutPremium - s.boughtPutPremium;
        const netPremium = netCallPremium + netPutPremium;
        const netDirectionalVolume =
            s.boughtCalls + s.soldPuts - (s.soldCalls + s.boughtPuts);
        return {
            ...s,
            netPremium,
            netDirectionalVolume,
        };
    });

    return universalStrikeSort(rows);
}

export function filterStrikesByWindow<T extends { strike: string }>(
    sortedRows: T[],
    selectedStrikes: "10" | "20" | "30" | "all" | "custom",
    customStrikeFrom?: string,
    customStrikeTo?: string,
): T[] {
    if (selectedStrikes === "all") return sortedRows;
    if (selectedStrikes === "custom") {
        const fromStrike = parseFloat(customStrikeFrom || "") || 0;
        const toStrike = parseFloat(customStrikeTo || "") || Infinity;
        if (fromStrike > 0 || toStrike < Infinity) {
            return sortedRows.filter((r) => {
                const strike = parseFloat(r.strike) || 0;
                return strike >= fromStrike && strike <= toStrike;
            });
        }
        return sortedRows;
    }
    const strikeCount = parseInt(selectedStrikes, 10);
    if (!Number.isFinite(strikeCount) || strikeCount <= 0) return sortedRows;
    const middleIndex = Math.floor(sortedRows.length / 2);
    const startIndex = Math.max(0, middleIndex - Math.floor(strikeCount / 2));
    const endIndex = Math.min(sortedRows.length, startIndex + strikeCount);
    return sortedRows.slice(startIndex, endIndex);
}
